import { useQuery } from '@tanstack/react-query';
import { Percent, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { inventoryService } from '@/services/inventory';

/**
 * Surfaces GST-rate drift: when the platform HSN→GST master (kept current by
 * the FastGST sync) has moved but the tenant's items still carry the old rate.
 * Reads the existing HSN-rate audit (cached) and, when any item would change,
 * nudges the operator to run "Sync HSN rates". Renders nothing when in sync.
 */
export function HsnRateDriftBanner({ onSync }: { onSync: () => void }) {
  const { data } = useQuery({
    queryKey: ['inventory', 'hsn-rate-audit'],
    queryFn: () => inventoryService.hsnRateAudit(),
    staleTime: 2 * 60 * 1000,
  });

  const n = data?.summary.wouldChange ?? 0;
  if (n === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-3 rounded-xl border border-amber-300/50 bg-amber-50/50 px-4 py-3 dark:bg-amber-500/5">
      <Percent className="h-4 w-4 shrink-0 text-amber-500" />
      <div className="min-w-0 flex-1 text-[12.5px] text-foreground">
        <strong>{n}</strong> {n === 1 ? 'item has' : 'items have'} a GST rate that differs from the
        latest published rates. Review and sync to keep invoicing + export filing correct.
      </div>
      <Button size="sm" variant="outline" onClick={onSync} className="shrink-0">
        Review &amp; sync <ArrowRight className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}
